<template>
  <div class="column-mapper">
    <div class="column-mapper-header">
      <h3>Map CSV Columns to Course Fields</h3>
      <div class="actions">
        <button @click="autoMapColumns" class="btn btn-secondary">Auto-Map</button>
        <button @click="showSaveDialog" class="btn btn-primary">Save Mapping</button>
        <button @click="showLoadDialog" class="btn btn-primary">Load Saved Mapping</button>
      </div>
    </div>

    <div class="mapping-alert" v-if="missingRequiredFields.length">
      <div class="alert inline alert-warning">
        Missing required fields: {{ missingRequiredFields.join(', ') }}
      </div>
    </div>

    <div class="data-table">
      <table>
        <thead>
          <tr>
            <th v-for="header in headers" :key="header">
              <div class="column-header">
                <div class="csv-column">{{ header }}</div>
                <div class="select-container">
                  <select v-model="columnMapping[header]" class="mapping-select">
                    <option value="">-- Select field --</option>
                    <option v-for="field in availableFields" :key="field.value" :value="field.value">
                      {{ field.label }} {{ field.required ? '(Required)' : '' }}
                    </option>
                  </select>
                </div>
              </div>
            </th>
          </tr>
        </thead>
        <tbody>
          <tr v-for="(row, rowIndex) in displayData" :key="rowIndex">
            <td v-for="header in headers" :key="`${rowIndex}-${header}`">
              {{ row[header] }}
            </td>
          </tr>
        </tbody>
      </table>

      <div class="pagination">
        <button @click="prevPage" class="btn btn-sm" :disabled="currentPage === 1">
          Previous
        </button>
        <span class="page-info">
          Page {{ currentPage }} of {{ totalPages }}
        </span>
        <button @click="nextPage" class="btn btn-sm" :disabled="currentPage === totalPages">
          Next
        </button>
      </div>
    </div>

    <course-mapping-save-modal v-if="showSaveModal" :mapping="columnMapping" @close="showSaveModal = false"
      @save="saveMapping" />

    <course-mapping-load-modal v-if="showLoadModal" @close="showLoadModal = false" @load="loadMapping" />
  </div>
</template>

<script>
import CourseMappingSaveModal from './CourseMappingSaveModal.vue';
import CourseMappingLoadModal from './CourseMappingLoadModal.vue';
import { mapGetters } from 'vuex';

export default {
  components: {
    CourseMappingSaveModal,
    CourseMappingLoadModal
  },

  props: {
    headers: {
      type: Array,
      required: true
    },
    previewData: {
      type: Array,
      required: true
    },
    availableFields: {
      type: Array,
      required: true
    }
  },

  data() {
    return {
      showSaveModal: false,
      showLoadModal: false
    }
  },

  computed: {
    ...mapGetters(['schoolId']),

    columnMapping: {
      get() {
        return this.$store.state.courseImport.columnMapping;
      },
      set(value) {
        this.$store.dispatch('courseImport/SET_COURSE_IMPORT_COLUMN_MAPPING', value);
      }
    },

    currentPage: {
      get() {
        return this.$store.state.courseImport.currentPage;
      },
      set(value) {
        this.$store.dispatch('courseImport/SET_COURSE_IMPORT_PAGE', value);
      }
    },

    rowsPerPage() {
      return this.$store.state.courseImport.rowsPerPage;
    },

    requiredFields() {
      return this.availableFields
        .filter(field => field.required)
        .map(field => field.value);
    },

    missingRequiredFields() {
      const mappedFields = Object.values(this.columnMapping);
      return this.requiredFields.filter(field => !mappedFields.includes(field))
        .map(field => {
          const fieldObj = this.availableFields.find(f => f.value === field);
          return fieldObj ? fieldObj.label : field;
        });
    },

    totalPages() {
      return Math.max(1, Math.ceil(this.previewData.length / this.rowsPerPage));
    },

    displayData() {
      const start = (this.currentPage - 1) * this.rowsPerPage;
      const end = start + this.rowsPerPage;
      return this.previewData.slice(start, end);
    }
  },

  methods: {
    isRequiredField(field) {
      return this.requiredFields.includes(field);
    },

    autoMapColumns() {
      const newMapping = {};

      this.headers.forEach(header => {
        const normalizedHeader = header.toLowerCase().replace(/[^a-z0-9]/g, '');

        const match = this.availableFields.find(field => {
          const normalizedField = field.label.toLowerCase().replace(/[^a-z0-9]/g, '');
          return normalizedHeader === normalizedField ||
            normalizedHeader.includes(normalizedField) ||
            normalizedField.includes(normalizedHeader);
        });

        if (match) {
          newMapping[header] = match.value;
        }
      });

      this.columnMapping = { ...newMapping };
    },

    showSaveDialog() {
      this.showSaveModal = true;
    },

    showLoadDialog() {
      this.showLoadModal = true;
    },

    saveMapping(name) {
      this.$store.dispatch('courseImport/SAVE_COURSE_IMPORT_MAPPING', {
        school_id: this.schoolId,
        name: name,
        mapping: this.columnMapping
      })
        .then(response => {
          this.$store.dispatch('ui/ADD_FLASH_MESSAGE', {
            type: 'success',
            message: 'Mapping saved successfully'
          });
          this.showSaveModal = false;
        })
        .catch(error => {
          this.$store.dispatch('ui/ADD_FLASH_MESSAGE', {
            type: 'error',
            message: 'Failed to save mapping: ' + (error.response?.data?.detail || error.response?.data?.error || 'Unknown error')
          });
        });
    },

    loadMapping(mapping) {
      this.columnMapping = mapping.mapping;
      this.showLoadModal = false;
    },

    getMappingForImport() {
      return this.columnMapping;
    },

    validateMapping() {
      return this.missingRequiredFields.length === 0;
    },

    prevPage() {
      if (this.currentPage > 1) {
        this.currentPage--;
      }
    },

    nextPage() {
      if (this.currentPage < this.totalPages) {
        this.currentPage++;
      }
    }
  }
}
</script>

<style scoped>
.column-mapper-header {
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 24px;
}

.column-mapper h3 {
  margin: 0;
}

.actions {
  display: flex;
  gap: 8px;
}

.column-header {
  display: flex;
  flex-direction: column;
}

.csv-column {
  font-weight: bold;
  margin-bottom: 8px;
}

.mapping-select {
  width: 100%;
  padding: 8px;
  border: 1px solid #ccc;
  border-radius: 4px;
  background-color: white;
  font-size: 14px;
}

.alert {
  padding: 12px 16px;
  border-radius: 4px;
  margin-bottom: 16px;
}

.alert-warning {
  background-color: #fff3cd;
  color: #856404;
  border: 1px solid #ffeeba;
}

.data-table {
  overflow-x: auto;
  margin-top: 16px;
}

table {
  width: 100%;
  border-collapse: collapse;
  border: 1px solid #e0e0e0;
}

th,
td {
  padding: 12px;
  text-align: left;
  border-bottom: 1px solid #e0e0e0;
}

th {
  background-color: #f5f5f5;
}

tr:nth-child(even) {
  background-color: #f9f9f9;
}

.pagination {
  display: flex;
  justify-content: flex-end;
  align-items: center;
  margin-top: 16px;
  gap: 12px;
}

.page-info {
  font-size: 14px;
}

.btn {
  padding: 8px 16px;
  border: none;
  border-radius: 4px;
  cursor: pointer;
  font-size: 14px;
  transition: background-color 0.3s;
}

.btn:disabled {
  opacity: 0.5;
  cursor: not-allowed;
}

.btn-sm {
  padding: 6px 12px;
  font-size: 12px;
}

.btn-primary {
  background-color: #1976d2;
  color: white;
}

.btn-primary:hover:not(:disabled) {
  background-color: #1565c0;
}

.btn-secondary {
  background-color: #f5f5f5;
  color: #757575;
}

.btn-secondary:hover:not(:disabled) {
  background-color: #e0e0e0;
}

.select-container {
  position: relative;
}

.select-container::after {
  content: '';
  position: absolute;
  top: 50%;
  right: 10px;
  transform: translateY(-50%);
  width: 0;
  height: 0;
  border-left: 5px solid transparent;
  border-right: 5px solid transparent;
  border-top: 5px solid #555;
  pointer-events: none;
}
</style>
